import React, { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { FileUp, Mail, Plug, CheckCircle2, Clock, Gauge } from 'lucide-react';
import { useRest, restFetch, RCard, RHead, RestLoading } from './restui';
import { GoogleBusinessCard } from './GoogleBusinessCard';
import { VipInviteCard } from './VipInviteCard';
import {
  ToastMark, SquareMark, CloverMark, DoorDashMark, UberEatsMark, GrubhubMark,
  GoogleMark, YelpMark, OpenTableMark, InstagramMark,
} from '../../components/BrandIcons';

/* Connections Center — "connect here and see what happens". Honest states only:
   available / connected / requested / not_connected. A request notifies the Kitxens team. */

const MARKS = {
  toast: ToastMark, square: SquareMark, clover: CloverMark, doordash: DoorDashMark,
  ubereats: UberEatsMark, grubhub: GrubhubMark, google_business: GoogleMark,
  yelp: YelpMark, opentable: OpenTableMark, instagram: InstagramMark,
};

const CATEGORIES = [
  ['pos', 'Point of Sale (POS)', 'Punto de venta (POS)'],
  ['delivery', 'Delivery', 'Delivery'],
  ['presence', 'Reviews & Presence', 'Reseñas y presencia'],
  ['reservations', 'Reservations', 'Reservas'],
  ['marketing', 'Marketing', 'Marketing'],
  ['data', 'Data', 'Datos'],
];

function IntelligenceScore({ isEs, data }) {
  const score = data?.score;
  if (!score) return null;
  const items = [...(data.connections || [])].sort((a, b) => (b.earned - a.earned) || (b.points - a.points));
  const pct = Math.round(100 * score.value / score.max);
  return (
    <div className="mb-7 rounded-3xl bg-[#1A1A1A] text-white p-6 lg:p-8" data-testid="conn-intelligence-score">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 items-center">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold inline-flex items-center gap-2" style={{ color: 'var(--rest-accent)' }}>
            <Gauge className="w-3.5 h-3.5" /> Restaurant Intelligence
          </p>
          <p className="mt-3 text-[46px] font-semibold leading-none tabular-nums" data-testid="conn-score-value">
            {score.value}<span className="text-[22px] text-white/40"> / {score.max}</span>
          </p>
          <div className="mt-4 h-2.5 rounded-full bg-white/10 overflow-hidden max-w-sm">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'var(--rest-accent)' }} />
          </div>
          <p className="mt-4 text-[13.5px] text-white/70 leading-relaxed max-w-md">
            {isEs
              ? 'No estás conectando software. Estás haciendo tu restaurante más inteligente: cada conexión enseña a Rachel a entenderlo mejor.'
              : 'You are not connecting software. You are making your restaurant smarter: every connection teaches Rachel to understand it better.'}
          </p>
        </div>
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">{isEs ? 'Para mejorar tu inteligencia' : 'To improve your intelligence'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {items.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-[13px]" data-testid={`conn-score-item-${c.key}`}>
                <span className={`w-[18px] h-[18px] rounded flex items-center justify-center text-[11px] shrink-0 ${c.earned ? 'bg-emerald-500 text-white' : 'border border-white/25 text-transparent'}`}>✓</span>
                <span className={`truncate ${c.earned ? 'text-white' : 'text-white/50'}`}>{c.name}</span>
                <span className={`ml-auto font-bold tabular-nums shrink-0 ${c.earned ? 'text-emerald-400' : 'text-white/40'}`}>+{c.points}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, isEs }) {
  const map = {
    available: ['bg-emerald-50 text-emerald-700 border-emerald-200', isEs ? 'Disponible' : 'Available'],
    connected: ['bg-emerald-50 text-emerald-700 border-emerald-200', isEs ? 'Conectado' : 'Connected'],
    requested: ['bg-amber-50 text-amber-700 border-amber-200', isEs ? 'Solicitado' : 'Requested'],
    not_connected: ['bg-gray-50 text-gray-400 border-gray-200', isEs ? 'No conectado' : 'Not connected'],
  };
  const [cls, label] = map[status] || map.not_connected;
  return <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid="conn-status-pill">{label}</span>;
}

function ConnectionCard({ c, isEs, contact, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const Mark = MARKS[c.key];

  const request = async () => {
    setBusy(true); setErr(null);
    const r = await restFetch(`/connections/${c.key}/request`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }),
    });
    setBusy(false);
    if (r.ok) { setOpen(false); setNote(''); onChanged(); }
    else {
      const j = await r.json().catch(() => ({}));
      setErr(typeof j.detail === 'string' ? j.detail : (isEs ? 'No se pudo enviar' : 'Could not send'));
    }
  };

  const cancel = async () => {
    setBusy(true);
    await restFetch(`/connections/${c.key}/request`, { method: 'DELETE' });
    setBusy(false); onChanged();
  };

  return (
    <RCard className="p-4" testid={`conn-card-${c.key}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
          {Mark ? <Mark className="w-5 h-5" /> : (c.key === 'manual_imports' ? <FileUp className="w-4.5 h-4.5 text-gray-500" /> : <Mail className="w-4.5 h-4.5 text-gray-500" />)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14px] font-semibold text-gray-900">{c.name}</p>
            <span className="rounded-full bg-[var(--rest-accent-soft)] text-[var(--rest-accent)] px-2 py-0.5 text-[10px] font-bold tabular-nums" data-testid={`conn-points-${c.key}`}>+{c.points} pts</span>
            <StatusPill status={c.status} isEs={isEs} />
          </div>
          <p className="mt-1 text-[12px] text-gray-500 leading-relaxed">{isEs ? c.unlocks_es : c.unlocks_en}</p>

          {c.status === 'available' && (
            <Link to="/restaurant/reports" data-testid={`conn-open-reports-${c.key}`}
              className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[var(--rest-accent)] hover:underline">
              <CheckCircle2 className="w-3.5 h-3.5" /> {isEs ? 'Abrir Reports →' : 'Open Reports →'}
            </Link>
          )}

          {c.status === 'requested' && (
            <div className="mt-2.5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700">
                <Clock className="w-3.5 h-3.5" />
                {isEs ? 'Solicitud enviada — el equipo Kitxens te contactará.' : 'Request sent — the Kitxens team will contact you.'}
              </span>
              <button onClick={cancel} disabled={busy} data-testid={`conn-cancel-${c.key}`}
                className="text-[11.5px] text-gray-400 hover:text-gray-700 underline transition-colors">
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          )}

          {c.status === 'not_connected' && !open && (
            <button onClick={() => setOpen(true)} data-testid={`conn-connect-${c.key}`}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white px-4 py-1.5 text-[12px] font-bold transition-opacity">
              <Plug className="w-3.5 h-3.5" /> {isEs ? 'Conectar' : 'Connect'}
            </button>
          )}

          {c.status === 'not_connected' && open && (
            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid={`conn-form-${c.key}`}>
              <p className="text-[11.5px] text-gray-500">
                {isEs
                  ? 'Nuestro equipo hace la conexión contigo (necesitamos tus accesos a esta plataforma). Te contactamos en menos de 24h.'
                  : 'Our team sets up this connection with you (we need your access to this platform). We reach out within 24h.'}
              </p>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
                placeholder={isEs ? 'Opcional: cuéntanos algo (¿ya tienes cuenta?, ¿quién la gestiona?)…' : 'Optional: tell us anything (do you have an account? who manages it?)…'}
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px]"
                data-testid={`conn-note-${c.key}`} />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button onClick={request} disabled={busy} data-testid={`conn-send-${c.key}`}
                  className="rounded-full bg-gray-900 hover:bg-[var(--rest-accent)] text-white px-4 py-1.5 text-[12px] font-bold transition-colors disabled:opacity-50">
                  {busy ? '…' : (isEs ? 'Enviar solicitud' : 'Send request')}
                </button>
                <button onClick={() => setOpen(false)} className="text-[11.5px] text-gray-400 hover:text-gray-700 transition-colors">
                  {isEs ? 'Cerrar' : 'Close'}
                </button>
                <a href={`${contact?.whatsapp}?text=${encodeURIComponent(`Hola Kitxens, quiero conectar ${c.name} a mi Restaurant AI OS`)}`}
                  target="_blank" rel="noopener noreferrer" data-testid={`conn-whatsapp-${c.key}`}
                  className="ml-auto text-[11.5px] font-bold text-[#25D366] hover:underline">
                  {isEs ? 'O escríbenos por WhatsApp →' : 'Or message us on WhatsApp →'}
                </a>
              </div>
              {err && <p className="mt-1.5 text-[11.5px] text-red-600" data-testid={`conn-error-${c.key}`}>{err}</p>}
            </div>
          )}
        </div>
      </div>
    </RCard>
  );
}

export default function RestConnections() {
  const { isEs, me } = useOutletContext();
  const { data, loading, reload } = useRest('/connections');
  if (loading && !data) return <RestLoading />;
  const list = data?.connections || [];
  const requested = list.filter((c) => c.status === 'requested').length;

  return (
    <div data-testid="rest-connections">
      <RHead title={isEs ? 'Conexiones' : 'Connections'}
        sub={isEs
          ? 'Cada conexión hace a Rachel más inteligente. Tú pides, el equipo Kitxens conecta — sin fricción técnica.'
          : 'Every connection makes Rachel smarter. You ask, the Kitxens team connects — zero technical friction.'} />
      <IntelligenceScore isEs={isEs} data={data} />
      <GoogleBusinessCard isEs={isEs} me={me} />
      <VipInviteCard isEs={isEs} />
      {requested > 0 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800" data-testid="conn-requested-banner">
          {isEs
            ? `Tienes ${requested} solicitud(es) en curso. El equipo Kitxens te contactará para completar la conexión.`
            : `You have ${requested} request(s) in progress. The Kitxens team will contact you to complete the setup.`}
        </div>
      )}
      {CATEGORIES.map(([cat, en, es]) => {
        const items = list.filter((c) => c.category === cat && c.key !== 'google_business');
        if (!items.length) return null;
        return (
          <div key={cat} className="mb-7" data-testid={`conn-category-${cat}`}>
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? es : en}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {items.map((c) => <ConnectionCard key={c.key} c={c} isEs={isEs} contact={data?.contact} onChanged={reload} />)}
            </div>
          </div>
        );
      })}
      <RCard className="p-6 flex flex-wrap items-center gap-4" testid="conn-help-card">
        <img src="/kitxens-logo.png" alt="Kitxens" className="h-9 w-auto shrink-0" data-testid="conn-kitxens-logo" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-gray-900">{isEs ? '¿No ves la plataforma que usas?' : 'Don\u2019t see the platform you use?'}</p>
          <p className="mt-0.5 text-[12.5px] text-gray-500">{isEs ? 'Dinos cuál es y la evaluamos — construimos esto contigo.' : 'Tell us which one and we\u2019ll evaluate it — we build this with you.'}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <a href={data?.contact?.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="conn-help-whatsapp"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-[12.5px] font-bold hover:opacity-90 transition-opacity">WhatsApp</a>
          <a href={`mailto:${data?.contact?.email}?subject=Nueva%20conexi%C3%B3n%20para%20mi%20Restaurant%20AI%20OS`} data-testid="conn-help-email"
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-[12.5px] font-bold text-gray-700 hover:border-gray-500 transition-colors">Email</a>
        </div>
      </RCard>
    </div>
  );
}
